import { useState } from 'react';
import { Typography, Badge, Spin, Button, Tooltip, Input, Tag } from 'antd';
import { DownOutlined, ClockCircleOutlined, ArrowRightOutlined, EnvironmentOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import { CountryFlag } from '../Common/CountryFlag';
import { type EventWithCircuit } from '../../api/eventsService';
import { type Session, sessionsService } from '../../api/sessionsService';
import { type CircuitWithGeoJSON } from '../../api/circuitsService';
import { formatDateRange } from '../../utils/dateUtils';
import { CircuitPreview } from './CircuitPreview';

const { Text } = Typography;

interface EventListProps {
  events: EventWithCircuit[];
  selectedEventId?: string;
  onEventClick: (event: EventWithCircuit) => void;
  onGoToEvent?: (event: EventWithCircuit) => void;
  onShowOnMap?: (event: EventWithCircuit) => void;
  onEditEvent?: (event: EventWithCircuit) => void;
  loading?: boolean;
  theme?: 'light' | 'dark';
  circuitsCache?: Record<string, CircuitWithGeoJSON>;
}

export const EventList = ({ events, selectedEventId, onEventClick, onGoToEvent, onShowOnMap, onEditEvent, loading, theme = 'dark', circuitsCache = {} }: EventListProps) => {
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<Record<string, Session[]>>({});
  const [loadingSessions, setLoadingSessions] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Past':
        return '#888';
      case 'Current':
        return '#52c41a';
      case 'Future':
        return '#1890ff';
      default:
        return '#888';
    }
  };


  const handleToggleExpand = async (event: EventWithCircuit, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Must match the eventKey used in the map below (includes start_date for uniqueness)
    const eventKey = `${event.season_year}-${event.circuit_id}-${event.start_date}`;
    
    if (expandedEventId === eventKey) {
      setExpandedEventId(null);
    } else {
      setExpandedEventId(eventKey);
      
      // Fetch sessions if not already loaded
      if (!sessions[eventKey] && !loadingSessions[eventKey]) {
        setLoadingSessions(prev => ({ ...prev, [eventKey]: true }));
        
        try {
          // Fetch sessions (now embedded in events)
          const eventSessions = await sessionsService.getSessionsByEvent(
            event.season_year,
            event.circuit_id,
            event.start_date
          );
          // SessionsService now returns empty array on error, so always store result
          setSessions(prev => ({ ...prev, [eventKey]: eventSessions }));
        } finally {
          setLoadingSessions(prev => ({ ...prev, [eventKey]: false }));
        }
      }
    }
  };

  // Filter events based on search term
  const filteredEvents = events.filter((event) =>
    event.circuit.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Search Input - fixed at top */}
      <div style={{ padding: '8px', marginBottom: '8px', flexShrink: 0 }}>
        <Input
          placeholder="Search events..."
          prefix={<SearchOutlined style={{ color: theme === 'dark' ? '#888' : '#666' }} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          style={{
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff',
            borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#d9d9d9',
            color: theme === 'dark' ? '#fff' : '#000',
          }}
        />
      </div>
      
      {/* Scrollable events list */}
      <div
        className="events-list-scrollable"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '8px',
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin />
          </div>
        ) : (
          filteredEvents.map((event) => {
            const isSelected = event.circuit_id === selectedEventId;
            // Include start_date in key to ensure uniqueness (same circuit can have multiple events per year)
            const eventKey = `${event.season_year}-${event.circuit_id}-${event.start_date}`;
            const isExpanded = expandedEventId === eventKey;
            const eventSessions = sessions[eventKey] || [];
            const isLoadingSessions = loadingSessions[eventKey];
            
            return (
              <div
                key={eventKey}
                style={{
                  cursor: 'pointer',
                  padding: '0',
                  transition: 'all 0.3s ease',
                  backgroundColor: isSelected 
                    ? 'rgba(14, 68, 125, 0.15)' 
                    : 'transparent',
                  borderLeft: isSelected 
                    ? '4px solid #0E447D' 
                    : '4px solid transparent',
                  marginBottom: '8px',
                  borderRadius: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
              >
                {/* Main Event Item */}
                <div
                  onClick={() => onEventClick(event)}
                  style={{
                    padding: '16px',
                    width: '100%',
                    display: 'flex',
                    gap: '12px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = theme === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.03)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {/* Circuit Preview */}
                  <div
                    style={{
                      width: '90px',
                      height: '80px',
                      flexShrink: 0,
                    }}
                  >
                    <CircuitPreview 
                      circuitId={event.circuit_id} 
                      theme={theme}
                      stations={event.stations}
                      circuitData={circuitsCache[event.circuit_geojson_id || event.circuit_id]}
                    />
                  </div>

                  {/* Event Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Date and Status */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: '11px',
                          color: theme === 'dark' ? '#888' : '#666',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 500,
                        }}
                      >
                        {formatDateRange(event.start_date, event.end_date)}
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <Badge
                          status={event.status === 'Current' ? 'processing' : 'default'}
                          color={getStatusColor(event.status)}
                        />
                        {event.demo && (
                          <Tag color="orange" style={{ fontSize: '10px', lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                            Demo
                          </Tag>
                        )}
                        {event.legacy && (
                          <Tag color="default" style={{ fontSize: '10px', lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                            Legacy
                          </Tag>
                        )}
                        <DownOutlined
                          onClick={(e) => handleToggleExpand(event, e)}
                          style={{
                            fontSize: '10px',
                            color: theme === 'dark' ? '#888' : '#666',
                            transition: 'transform 0.3s ease',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            cursor: 'pointer',
                          }}
                        />
                      </div>
                    </div>

                    {/* Event Name and Flag */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '4px',
                      }}
                    >
                      <CountryFlag
                        country={event.circuit.country}
                        style={{
                          width: '24px',
                          height: '18px',
                          objectFit: 'cover',
                          borderRadius: '2px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }}
                      />
                      <Text
                        strong
                        style={{
                          fontSize: '14px',
                          color: isSelected ? '#0E447D' : (theme === 'dark' ? '#fff' : '#000'),
                          lineHeight: '1.4',
                        }}
                      >
                        {event.circuit.display_name}
                      </Text>
                    </div>

                    {/* Circuit Name */}
                    <Text
                      style={{
                        fontSize: '12px',
                        color: theme === 'dark' ? '#aaa' : '#888',
                        display: 'block',
                        marginLeft: '34px',
                      }}
                    >
                      {event.name}
                    </Text>

                    {/* Action Buttons */}
                    {(onShowOnMap || onGoToEvent || onEditEvent) && (
                      <div
                        style={{
                          marginTop: '8px',
                          marginLeft: '34px',
                          display: 'flex',
                          gap: '8px',
                        }}
                      >
                        {onShowOnMap && (
                          <Tooltip title="Show on Map">
                            <Button
                              type="primary"
                              size="small"
                              icon={<EnvironmentOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onShowOnMap(event);
                              }}
                              style={{
                                background: '#0E447D',
                                borderColor: '#0E447D',
                                fontSize: '11px',
                                height: '24px',
                                width: '24px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            />
                          </Tooltip>
                        )}
                        {onEditEvent && !event.legacy && (
                          <Tooltip title="Edit Event">
                            <Button
                              type="primary"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditEvent(event);
                              }}
                              style={{
                                background: '#0E447D',
                                borderColor: '#0E447D',
                                fontSize: '11px',
                                height: '24px',
                                width: '24px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            />
                          </Tooltip>
                        )}
                        {onGoToEvent && (
                          <Button
                            type="primary"
                            size="small"
                            icon={<ArrowRightOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onGoToEvent(event);
                            }}
                            style={{
                              background: '#0E447D',
                              borderColor: '#0E447D',
                              fontSize: '11px',
                              height: '24px',
                              padding: '0 12px',
                            }}
                          >
                            Go to Event
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Expandable Sessions Section */}
                {isExpanded && (
                  <div
                    style={{
                      padding: '0 16px 16px 16px',
                      borderTop: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                      backgroundColor: theme === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
                    }}
                  >
                    {isLoadingSessions ? (
                      <div style={{ textAlign: 'center', padding: '16px' }}>
                        <Spin size="small" />
                      </div>
                    ) : eventSessions.length > 0 ? (
                      <div style={{ marginTop: '12px' }}>
                        <Text
                          style={{
                            fontSize: '11px',
                            color: theme === 'dark' ? '#888' : '#666',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontWeight: 600,
                            display: 'block',
                            marginBottom: '8px',
                          }}
                        >
                          Sessions Schedule
                        </Text>
                        {eventSessions.map((session) => {
                          // Get circuit timezone for proper time display
                          const circuitTimeZone = event.circuit?.time_zone;
                          
                          return (
                            <div
                              key={session.session_name}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                marginBottom: '4px',
                                borderRadius: '4px',
                                backgroundColor: theme === 'dark' 
                                  ? 'rgba(255,255,255,0.03)' 
                                  : 'rgba(0,0,0,0.02)',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px' }}>
                                  {sessionsService.getSessionIcon(session.session_name)}
                                </span>
                                <div>
                                  <Text
                                    style={{
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      color: theme === 'dark' ? '#fff' : '#000',
                                      display: 'block',
                                    }}
                                  >
                                    {sessionsService.getSessionDisplayName(session.session_name)}
                                  </Text>
                                  <Text
                                    style={{
                                      fontSize: '10px',
                                      color: theme === 'dark' ? '#888' : '#666',
                                    }}
                                  >
                                    {sessionsService.formatSessionDate(session.start_time, circuitTimeZone)}
                                  </Text>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <ClockCircleOutlined 
                                  style={{ 
                                    fontSize: '10px',
                                    color: theme === 'dark' ? '#888' : '#666',
                                  }} 
                                />
                                <Text
                                  style={{
                                    fontSize: '11px',
                                    color: theme === 'dark' ? '#aaa' : '#666',
                                    fontFamily: 'monospace',
                                  }}
                                >
                                  {sessionsService.formatSessionTime(session.start_time, circuitTimeZone)}
                                </Text>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <Text
                        style={{
                          fontSize: '11px',
                          color: theme === 'dark' ? '#666' : '#999',
                          display: 'block',
                          textAlign: 'center',
                          padding: '16px',
                        }}
                      >
                        No sessions scheduled
                      </Text>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

